/*
  The room: three columns on a desk, one column on a phone.

  Rail | room | board. The room is the only part that grows, because it is
  the part that fills with people.
*/
/*
  *** A PAGE INSIDE THE SHELL, NOT A SHELL OF ITS OWN ***
  This was built as a full-window layout with its own dark left column,
  which fought the shell's sidebar: hiding the sidebar made the top bar
  span the whole window, and keeping both gave two dark columns. It is a
  page like any other now — the shell draws the sidebar and header, and
  these three columns live inside the content area.
*/
.page {
  /*
    Two columns: the room, and the leaderboard beside it. The rail that
    used to be a third column is the SIDEBAR now — see LibraryFocusRail —
    so there is one dark left column on this screen, as on every other.
  */
  display: grid;
  grid-template-columns: minmax(0, 1fr) 17rem;
  grid-template-areas: "main board";
  gap: var(--space-5);
  align-items: stretch;
}

/* ------------------------------------------------------------ left rail */










/* ------------------------------------------------------------ the room */
/*
  *** CAPPED, NOT STRETCHED ***
  On a wide monitor an uncapped centre column leaves one tile floating in a
  1200px field and a chat box running edge to edge. The cap keeps the room
  the shape of a room; `margin-inline: auto` centres it in whatever space is
  left over.
*/
/*
  No padding of its own: the shell's `.content` already pads the page, and
  a second inset pushed the room's title out of line with every other
  page's heading. No max-width either — the grid's own column sizing is
  what shapes this, and capping it here left the room floating.
*/
.main {
  grid-area: main;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  min-width: 0;
}

.head {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.title {
  margin: 0;
  font-size: var(--text-2xl);
  font-weight: 600;
  color: var(--text);
}

.sub {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--text-muted);
}

/*
  The same panel treatment as the chat, so the room reads as two blocks
  rather than a run of loose headings.
*/
.peopleBlock {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
}

.blockTitle {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin: 0;
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-subtle);
}

.count {
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-full);
  padding: 0 0.4rem;
  letter-spacing: 0;
  color: var(--text-muted);
}

.empty {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--text-muted);
}

/*
  *** THE GRID IS SIZED FOR CAMERA TILES ***
  auto-fill at a fixed minimum, so one person and forty people both look
  deliberate. 9rem is about a 144p tile at its natural size, which is the
  resolution this grid is meant to carry.
*/
.tiles {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  /*
    auto-fill with a MAX: one tile in a twelve-column grid looked abandoned,
    but auto-fit would have stretched that single tile across the whole row.
    Columns stay tile-sized whether there is one person or forty.
  */
  grid-template-columns: repeat(auto-fill, minmax(8rem, 10rem));
  gap: var(--space-3);
}

.tile {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
  padding: 0;
  background: none;
  border: none;
  font: inherit;
  cursor: pointer;
  text-align: left;
}

/*
  The camera slot. Holds its 4:3 shape whether it contains an avatar or a
  video element, so wiring LiveKit changes what is inside the box and not
  the layout around it.
*/
.slot {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 4 / 3;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
}

.tileMentor .slot {
  border-color: var(--indigo);
  box-shadow: 0 0 0 1px var(--indigo);
}

.avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: var(--radius-full);
  background: var(--indigo-soft);
  color: var(--indigo);
  font-size: var(--text-md);
  font-weight: 600;
}

/* Present means present: the dot is the whole point of a presence tile. */
.liveDot {
  position: absolute;
  right: 0.375rem;
  bottom: 0.375rem;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: var(--radius-full);
  background: var(--success);
}

.tileName {
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tileTime {
  font-size: var(--text-xs);
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}

/* ----------------------------------------------------------- the board */
.board {
  grid-area: board;
  padding: var(--space-5) var(--space-4);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  /* Below the shell's top bar, like the rail — see the note there. */
  position: sticky;
  top: calc(var(--topbar-height) + var(--space-4));
  height: calc(100vh - var(--topbar-height) - var(--space-6));
  overflow-y: auto;
}

/* ---------------------------------------------------------- responsive */
/*
  Three steps, not one. A single jump from three columns to one leaves every
  tablet rendering a 15rem rail, a squeezed room and a 19rem board side by
  side — the worst of both. px to match the breakpoints the rest of the app
  already uses (640 / 900 / 1024).
*/

/* Laptop: the board narrows before anything is dropped. */
@media (max-width: 1200px) {
  .page {
    grid-template-columns: 13rem minmax(0, 1fr) 16rem;
  }
}

/*
  Tablet: the board moves BELOW the room rather than being squeezed. The
  rail stays, because Leave and the timer are the two things that must
  always be reachable.
*/
@media (max-width: 1024px) {
  .page {
    grid-template-columns: 13rem minmax(0, 1fr);
    grid-template-areas:
      "rail main"
      "rail board";
    align-content: start;
  }

  .main {
    grid-area: main;
  }

  .board {
    grid-area: board;
    position: static;
    height: auto;
    border-left: none;
    border-top: 1px solid var(--border);
    /* Matches the room's padding so the board lines up under it. */
    padding: var(--space-5);
  }
}

/*
  Phone: one column, and the rail becomes a horizontal bar at the TOP. A
  100vh sidebar above the content would be a full screen of nothing before
  a student reaches the room.
*/
@media (max-width: 640px) {
  .page {
    grid-template-columns: 1fr;
    grid-template-areas:
      "rail"
      "main"
      "board";
  }

  .main {
    padding: var(--space-4);
    gap: var(--space-4);
  }

  .title {
    font-size: var(--text-xl);
  }

  /* Smaller tiles so three still fit across a phone rather than one and a
     half. Still 4:3, so the camera slot is unaffected. */
  .tiles {
    grid-template-columns: repeat(auto-fill, minmax(6.5rem, 1fr));
    gap: var(--space-2);
  }

  .avatar {
    width: 2.25rem;
    height: 2.25rem;
    font-size: var(--text-sm);
  }

  .board {
    padding: var(--space-4);
  }
}

/* ------------------------------------------------- mentors on duty */
/*
  Named, not tiled. A student needs to know a mentor is reachable before
  deciding to ask, and a mentor needs to see which colleagues are already
  in the room. In a grid of forty tiles the one mentor is just another
  square, so they get their own strip above it.
*/
.dutyBlock {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--indigo-soft);
  border: 1px solid var(--indigo);
  border-radius: var(--radius-lg);
}

.dutyList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
}

.duty {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3) var(--space-2) var(--space-2);
  background: var(--bg-elevated);
  border: 1px solid var(--indigo);
  border-radius: var(--radius-full);
  font: inherit;
  cursor: pointer;
  text-align: left;
}

.duty:hover {
  border-color: var(--indigo-dark);
  box-shadow: 0 0 0 1px var(--indigo);
}

.dutyAvatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  border-radius: var(--radius-full);
  background: var(--indigo);
  color: #fff;
  font-size: var(--text-xs);
  font-weight: 600;
}

.dutyText {
  display: flex;
  flex-direction: column;
  gap: 0.0625rem;
  min-width: 0;
}

.dutyName {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text);
}

.dutyYou {
  background: var(--indigo);
  color: #fff;
  border-radius: var(--radius-full);
  padding: 0 0.375rem;
  font-size: var(--text-xs);
  font-weight: 600;
}

.dutyTime {
  font-size: var(--text-xs);
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}

/* --------------------------------------------------- the duty timer */



/* --------------------------------------------- the docked chat */
/*
  Fixed to the corner, outside the grid, so it costs the tile columns
  nothing. Insets are added to the safe area rather than replacing it, or
  the card sits under a phone's home indicator.
*/
.dock {
  position: fixed;
  z-index: 40;
  right: max(var(--space-4), env(safe-area-inset-right, 0px));
  bottom: max(var(--space-4), env(safe-area-inset-bottom, 0px));
  width: 22rem;
  max-width: calc(100vw - var(--space-6));
  box-shadow: var(--shadow-lg);
  border-radius: var(--radius-lg);
}

.pill {
  position: fixed;
  z-index: 40;
  right: max(var(--space-4), env(safe-area-inset-right, 0px));
  bottom: max(var(--space-4), env(safe-area-inset-bottom, 0px));
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: var(--indigo);
  color: #fff;
  border: none;
  border-radius: var(--radius-full);
  box-shadow: var(--shadow-lg);
  font: inherit;
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
}

.pill:hover {
  background: var(--indigo-dark);
}

@media (max-width: 40rem) {
  /* Full width on a phone: a 22rem card beside a 24rem screen is a sliver. */
  .dock {
    left: max(var(--space-4), env(safe-area-inset-left, 0px));
    width: auto;
  }
}

/* ------------------------------------- nobody on duty right now */
/*
  The card stays; only its tone drops. An absent card left a student
  unable to tell "no mentor right now" from "this room has none at all",
  and only the first is worth waiting through.
*/
.dutyEmpty {
  background: var(--bg-elevated);
  border-color: var(--border);
}

.dutyNone {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--text-muted);
  line-height: var(--leading-normal);
}
